import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { DateTools, TTableRow } from '@treezor/design-system';

import { DashboardHelpers } from '@/utils/DashboardHelpers';

import { PaymentCardStatus } from '@/features/cards/components/CardDetails/PaymentCardStatus';
import { CardType } from '@/features/cards/components/AllCards/CardType';
import { EmbossedName } from '@/features/cards/components/AllCards/EmbossedName';
import { IPaymentCard } from '@/features/cards/store/models/card.model';
import { ALL_CARDS_PER_PAGE } from '@/features/cards/constants';
import { allcardsHeadersList, TAllCardsRowType } from '@/features/cards/store/models/allcards.model';

import { CellLink } from '@/ui-kit/table-filters/CellLink';
import { RowSkeleton } from '@/ui-kit/loaders/RowSkeleton';

interface IAllCardsRowsProps {
  paymentCards?: IPaymentCard[];
  isLoading?: boolean;
}

export function AllCardsRows({ paymentCards = [], isLoading = false }: IAllCardsRowsProps) {
  const navigate = useNavigate();

  const handleNavigateToProfile = useCallback(
    (card, type: 'card' | 'wallet') => {
      navigate(
        DashboardHelpers.generateProfilePath(
          card.userId!,
          type === 'card' ? { cardId: card.cardId } : { walletId: card.walletId },
        ),
      );
    },
    [navigate],
  );

  if (isLoading) {
    return RowSkeleton<TAllCardsRowType>({ headers: allcardsHeadersList, length: ALL_CARDS_PER_PAGE });
  }

  return paymentCards?.map(
    (paymentCard: IPaymentCard): TTableRow<TAllCardsRowType> => ({
      publicToken: (
        <div className="flex flex-col justify-start" data-testid="publicToken">
          <span
            data-testid={'publicToken-link'}
            className="w-fit typo-body-semibold-small cursor-pointer hover:text-interactive"
            onClick={() => handleNavigateToProfile(paymentCard, 'card')}
          >
            {paymentCard.publicToken}
          </span>
          <span className="typo-body-regular-caption">ID #{paymentCard.cardId}</span>
        </div>
      ),
      statusCode: (
        <div className="text-center" data-testid="statusCode">
          <PaymentCardStatus paymentCard={paymentCard} />
        </div>
      ),
      virtualConverted: <CardType paymentCard={paymentCard} />,
      walletId: (
        <CellLink
          label={`${paymentCard.walletId}`}
          handleNavigate={() => handleNavigateToProfile(paymentCard, 'wallet')}
          testId="walletId"
        />
      ),
      embossedName: <EmbossedName paymentCard={paymentCard} />,
      startDate: <>{paymentCard.isLive ? paymentCard.startDate : '/'}</>,
      expiryDate: <>{DateTools.format(paymentCard.expiryDate)}</>,
      createdDate: <>{DateTools.format(paymentCard.createdDate)}</>,
      actions: <></>,
      onClick: () => handleNavigateToProfile(paymentCard, 'card'),
    }),
  );
}
